import { useState, useEffect, useMemo } from 'react';

import { isExperimentEnabled } from '@confluence/feature-experiments';
import {
	confluenceLocalStorageInstance as localStorage,
	confluenceSessionStorageInstance as sessionStorage,
} from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY = 'admin.announcement.banner';
const PERSISTED_ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY =
	PERSISTED_KEYS_ON_SERVER.PERSISTED_ADMIN_ANNOUNCEMENT_BANNER;

type DismissalStateParam = {
	isLicensed: boolean;
	id?: string;
	updatedAt?: string;
	isDismissible?: boolean;
};

export const getIsDismissed = ({
	cachedUpdatedAt,
	updatedAt,
	isDismissible,
}: {
	cachedUpdatedAt?: string;
	updatedAt?: string;
	isDismissible?: boolean;
}): boolean => {
	if (!updatedAt) return false;
	if (!isDismissible && isExperimentEnabled('confluence_aab_client_side_dismissible_check')) {
		return false;
	}
	return cachedUpdatedAt === updatedAt;
};

export const useDismissalState = ({
	isLicensed,
	id,
	updatedAt,
	isDismissible,
}: DismissalStateParam): {
	isDismissed: boolean | undefined;
	onDismiss: () => void;
} => {
	const { storage, dismissStateKey, initialUpdatedAt } = useMemo(() => {
		if (!isLicensed) {
			return {
				storage: sessionStorage,
				dismissStateKey: ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY,
			};
		}

		return {
			storage: localStorage,
			dismissStateKey: PERSISTED_ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY,
			initialUpdatedAt: localStorage?.getItem(PERSISTED_ADMIN_ANNOUNCEMENT_BANNER_DISMISS_STATE_KEY)
				?.updatedAt,
		};
	}, [isLicensed]);

	const [isDismissed, setIsDismissed] = useState<boolean | undefined>(
		getIsDismissed({ cachedUpdatedAt: initialUpdatedAt, updatedAt, isDismissible }),
	);

	useEffect(() => {
		if (updatedAt) {
			const cachedUpdatedAt = storage.getItem(dismissStateKey)?.updatedAt;
			setIsDismissed(getIsDismissed({ cachedUpdatedAt, updatedAt, isDismissible }));
		}
	}, [dismissStateKey, storage, updatedAt, isDismissible]);

	const onDismiss = (): void => {
		storage.setItem(dismissStateKey, { id, updatedAt });
		const cachedUpdatedAt = storage.getItem(dismissStateKey)?.updatedAt;
		setIsDismissed(getIsDismissed({ cachedUpdatedAt, updatedAt, isDismissible }));
	};

	return { isDismissed, onDismiss };
};
